import { createReducer } from 'app/state/utils'
import * as types from './types'

/** The header's two warnings, computed on the server by the header's rules (ADR_LOS-030). */
export interface TradeHeaderFacts {
  notRefreshedCount: number
  hasUnappliedPIK: boolean
  hasUnappliedPaydown: boolean
}

/** An entity box on the Overview tab. */
export interface TradeOverviewEntity {
  name: string | null
  mei: string | null
  entityType: string | null
  systemId: string | null
  internalId: string | null
}

/** One row of the Overview tab's Traded Assets table. */
export interface TradeOverviewFacility {
  facilityId: string
  tradeAmount: string
  price: string
  currentAmount: string | null
  name: string | null
  status: string | null
  facilityType: string | null
  baseCurrency: string | null
  cusip: string | null
  lxId: string | null
  systemId: string | null
  clientId: string | null
}

/**
 * The Overview tab's view of ONE trade (ADR_LOS-030):
 * `GET /api/trade-detail/:id/overview`, one request running one statement.
 * The master trade itself goes to the master-trade store (the header, the
 * actions menu and the edit forms read it there, and edits update it in
 * place); the rest — what the tab joins onto it, and the header's two
 * warnings — lives here.
 */
export interface TradeOverview {
  masterTradeId: string
  myEntity: TradeOverviewEntity
  counterparty: TradeOverviewEntity
  agent: TradeOverviewEntity
  deal: {
    name: string | null
    systemId: string | null
    cusip: string | null
    lxId: string | null
  }
  /** Ordered by facility name, case-insensitively. */
  facilities: TradeOverviewFacility[]
  /** The latest settle date of a settled sub trade (YYYY-MM-DD), or null. */
  closeDate: string | null
  hasSettledSubTrades: boolean
  header: TradeHeaderFacts
  /**
   * Only from the Activity tab's read (LOS-7095): the trade's sub trades as the note
   * composer's picker needs them, ordered by systemId then id. Kept here, not in the shared
   * sub-trades store, which holds complete sub-trade records for the Sub-trade tab.
   */
  subTrades?: TradeActivitySubTrade[]
}

/** One sub trade as the Activity Stream composer's picker needs it (LOS-7095). */
export interface TradeActivitySubTrade {
  id: string
  systemId: string | null
  status: string
}

/**
 * The Sub-trade tab's lookups for ONE trade, from `GET /api/trade-detail/:id`
 * (ADR_LOS-030 step 2): its deal, the deal's facilities, the entities it
 * references (both sides of the trade and of each sub trade, the
 * facilities' agents), their types and wire instructions; the fully signed
 * AA metadata the tab used to ask the ledger for, and every AA metadata
 * (the Documents tab's not-refreshed check); the header's warnings.
 *
 * Its own slice, NOT the shared deal / facility / entity stores: those are
 * read by dozens of components on other pages as the whole tenant, so one
 * trade's slice must never stand in for it there. The tab reads these merged
 * with the shared stores (`detail-trade/use-trade-page-lookups.ts`), so an
 * editor that loads a whole list on open adds to what the tab shows.
 */
export interface TradeDetailLookups {
  masterTradeId: string | null
  deals: any[]
  facilities: any[]
  entities: any[]
  entityTypes: any[]
  wireInstructions: any[]
  fullySignedAaMetadata: any[]
  /** Every AA metadata of the sub trades, signed or not: the Documents tab's not-refreshed check. */
  aaMetadata: any[]
  header: TradeHeaderFacts | null
}

export interface TradeDetailState {
  overview: TradeOverview | null
  lookups: TradeDetailLookups
}

const emptyLookups: TradeDetailLookups = {
  masterTradeId: null,
  deals: [],
  facilities: [],
  entities: [],
  entityTypes: [],
  wireInstructions: [],
  fullySignedAaMetadata: [],
  aaMetadata: [],
  header: null,
}

const initialState: TradeDetailState = { overview: null, lookups: emptyLookups }

const tradeDetailReducer = createReducer(initialState)({
  [types.SET_TRADE_OVERVIEW]: (
    state: TradeDetailState,
    action: { payload: TradeOverview },
  ) => ({ ...state, overview: action.payload }),
  [types.SET_TRADE_DETAIL_LOOKUPS]: (
    state: TradeDetailState,
    action: { payload: TradeDetailLookups },
  ) => ({ ...state, lookups: action.payload }),
})

export default tradeDetailReducer
